import { useState, useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import CircularBadge from "../badge";
import ForgeLogo from "../logo";
import "./CTAsec.css";
import { PiMountainsFill } from "react-icons/pi";
import { FaCampground, FaHandsHelping } from "react-icons/fa";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease, delay } },
});

/* ── Swatch colours — pulled from client master.css palette ── */
const SWATCHES = [
  { bg: "#e8dcc8", texture: "topo", icon: PiMountainsFill },
  { bg: "#c8b89a", texture: "", icon: FaHandsHelping },
  { bg: "var(--c-forest)", texture: "stone", icon: FaCampground },
  { bg: "#4a5e42", texture: "", icon: PiMountainsFill },
  { bg: "var(--c-rust)", texture: "stone", icon: FaHandsHelping },
  { bg: "var(--c-ink)", texture: "", icon: FaCampground },
];

export default function CTAsec() {
  const [email, setEmail] = useState("");
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  const photoRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: photoRef,
    offset: ["start end", "end start"],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], ["-10%", "20%"]);

  const flowerRef = useRef(null);

  const { scrollYProgress: flowerScroll } = useScroll({
    target: flowerRef,
    offset: ["start end", "end start"],
  });

  const flowerY = useTransform(flowerScroll, [0, 1], ["-8%", "28%"]);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Journey sign-up:", email);
  };

  return (
    <footer className="ftr" id="donate" ref={ref}>
      {/* ── Two-panel CTA ── */}
      <div className="ftr__cta">
        {/* Botanical illustration — left edge */}

        {/* Left panel — Start Your Journey */}
        <motion.div className="ftr__panel position-relative" variants={fadeUp(0.06)} initial="hidden" animate={inView ? "show" : "hidden"}>
          <div className="innerContent w-100 d-flex justify-content-end">
            <div>
              <h2 className="ftr__headline u-display ftr__headline--forest">
                Start Your
                <br />
                Journey
              </h2>
              <p className="ftr__sub">
                Take the first step.
                <br />
                We'll walk with you.
              </p>
              <form className="ftr__form " onSubmit={handleSubmit}>
                <label htmlFor="ftr-email" className="visually-hidden">
                  Email address
                </label>
                <input id="ftr-email" type="email" inputMode="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} className="ftr__input" required />
                <button type="submit" className="ftr__btn ftr__btn--filled">
                  Get Retreat Info
                </button>
              </form>
            </div>
          </div>

          <motion.div className="ftr__illustration ftr__illustration--left" ref={flowerRef} variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <motion.img src="./assets/flower.png" alt="" style={{ y: flowerY }} />
          </motion.div>
        </motion.div>

        {/* OR divider */}
        <motion.div className="ftr__or" aria-hidden="true" variants={fadeUp(0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
          <span className="ftr__or-line" />
          <span className="ftr__or-circle">OR</span>
          <span className="ftr__or-line" />
        </motion.div>

        {/* Right panel — Support the Mission */}
        <motion.div className="ftr__panel position-relative" variants={fadeUp(0.14)} initial="hidden" animate={inView ? "show" : "hidden"}>
          <div className="innerContent">
            <h2 className="ftr__headline u-display ftr__headline--rust">
              Support
              <br />
              the Mission
            </h2>
            <p className="ftr__sub">
              Change happens because
              <br />
              of people like you.
            </p>
            <a href="#donate" className="ftr__btn ftr__btn--outline">
              Donate Today
            </a>
          </div>

          <motion.div className="ftr__illustration ftr__illustration--right" ref={photoRef} variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <motion.img src="./assets/retreats/hutFace.png" alt="" style={{ y: photoY }} />
          </motion.div>
        </motion.div>

        {/* Retreat illustration — right edge */}
      </div>

      {/* ── Colour swatch band ── */}
      <div className="ftr__swatches">
        {/* Left meta block */}
        <div className="ftr__swatches-meta">
          <span>FORGE</span>
          <span>RETREAT</span>
          <span>COLOMBIA</span>
          <span className="ftr__swatches-cross">— +</span>
        </div>

        {/* Six colour tiles */}
        {SWATCHES.map((s, i) => (
          <div key={i} className={`ftr__swatch ${s.texture ? `ftr__swatch--${s.texture}` : ""}`} style={{ background: s.bg }}>
            <s.icon size={40} color="var(--c-rust)" style={{ mixBlendMode: "multiply" }} />
          </div>
        ))}

        {/* Right: Forge logo + circular badge */}
        <div className="ftr__swatches-brand">
          <ForgeLogo fill="var(--c-rust)" width={38} height={38} />
          <CircularBadge colors="var(--c-rust)" logoSize={26} />
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="ftr__bar container-fg container-2xxl">
        <span className="ftr__bar-copy">© {new Date().getFullYear()} Forge Retreat. All rights reserved.</span>
        <nav className="ftr__bar-nav" aria-label="Footer navigation">
          {["Privacy Policy", "Terms", "Contact"].map((l) => (
            <a key={l} href={`#${l.toLowerCase().replace(/\s/g, "-")}`} className="ftr__bar-link">
              {l}
            </a>
          ))}
        </nav>
      </div>
    </footer>
  );
}
